import gql from "graphql-tag";
import {
  PdpCarouselsResult,
  ProductGender,
  CarouselRecommendTypeInput,
} from "src/types/graphql.d";
import { ApolloError } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import { useFeatureFlags } from "./useFeatureFlags";

interface PdpCarouselsVars {
  slug: string;
  shop_type: ProductGender;
  limit: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
}

interface PdpCarouselsResponse {
  pdpCarousels: PdpCarouselsResult | undefined;
}

interface PdpCarouselsHook {
  data: PdpCarouselsResponse | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

const PDP_CAROUSELS_QUERY = gql`
  query pdpCarousels(
    $slug: String!
    $shop_type: ProductGender!
    $limit: Int!
    $carousel_recommend_type: CarouselRecommendTypeInput
  ) {
    pdpCarousels(
      slug: $slug
      shop_type: $shop_type
      limit: $limit
      carousel_recommend_type: $carousel_recommend_type
    ) {
      carousels {
        title
        id
        target_link
        target_type
        products {
          id
          name
          slug
          price
          regular_price
          brand {
            id
            name
            designer_brand
          }
          thumbnail
          labels {
            type
            value
            color
            priority
          }
          available_sizes
          available_sizes_label
          currency {
            code
            symbol
            position
          }
          user_wishlist_item_id
          gate {
            level
          }
          images {
            url
            device
            shop_type
          }
          product_kids_gender
          product_shop_type
        }
        response_source_type
      }
    }
  }
`;

const DEFAULT_LIMIT = 12;
const usePdpCarousels = ({
  slug,
  shopType,
  limit = DEFAULT_LIMIT,
  carousel_recommend_type,
}: {
  slug: string;
  shopType: ProductGender;
  limit?: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
}): PdpCarouselsHook => {
  const { pdpCarouselRecommender, pdpTrendingCarouselRecommender } =
    useFeatureFlags();
  const variables = {
    slug,
    shop_type: shopType,
    limit,
    carousel_recommend_type: {
      pdp_similar_items_carousel:
        typeof pdpCarouselRecommender === "string"
          ? pdpCarouselRecommender
          : null,
      pdp_trending_items_carousel:
        typeof pdpTrendingCarouselRecommender === "string"
          ? pdpTrendingCarouselRecommender
          : null,
    },
  };

  const { data, loading, error } = useQuery<
    PdpCarouselsResponse,
    PdpCarouselsVars
  >(PDP_CAROUSELS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    ssr: true,
  });

  return {
    data,
    loading,
    error,
  };
};

export default usePdpCarousels;
